import React, { useMemo, useRef, useLayoutEffect, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, FormControl, FormControlLabel, MenuItem, Select } from '@mui/material';

import { thousandFormat2 } from '../../../../Utils/Functions';

const ROW_HEIGHT = 23;
const AXIS_RIGHT_PADDING = 28;
const MIN_NAME_WIDTH = 285;
const MAX_NAME_WIDTH = 430;
const MIN_DIRECTOR_WIDTH = 95;
const MAX_DIRECTOR_WIDTH = 150;
const MIN_TYPE_WIDTH = 70;
const MAX_TYPE_WIDTH = 150;

const PROBABILITY_PALETTES = [
  { active: '#dbe6f2', inactive: '#eef3f8' },
  { active: '#a9bfd5', inactive: '#dde7f0' },
  { active: '#6f93b8', inactive: '#c7d5e4' },
  { active: '#466a91', inactive: '#afc0d1' },
];

function clamp(value, min, max) { return Math.max(min, Math.min(max, value)); }
function estimateTextWidth(text, charWidth = 7.2, padding = 26) { return Math.ceil((`${text || ''}`.length * charWidth) + padding); }
function useMeasuredWidth(ref) {
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    if (!ref.current) return undefined;
    const update = () => setWidth(ref.current?.offsetWidth || 0);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(ref.current);
    window.addEventListener('resize', update);
    return () => { observer.disconnect(); window.removeEventListener('resize', update); };
  }, [ref]);
  return width;
}
function shortDirectorName(name) { if (!name) return ''; const parts = `${name}`.trim().split(/\s+/); return parts.length >= 2 ? `${parts[0]} ${parts[1]}` : name; }
function getProbabilityBucket(probabilityOfSuccess) {
  const value = Number(probabilityOfSuccess || 0);
  const normalized = value > 1 ? value / 100 : value;
  if (normalized > 0.75) return 3;
  if (normalized > 0.50) return 2;
  if (normalized > 0.25) return 1;
  return 0;
}
function getTypeLabel(item) {
  return (
    item.OpportunityTypeName ||
    item.ProjectTypeCode ||
    item.ProjectTypeName ||
    item.ProjectType ||
    item.Type ||
    item.OpportunityTypeCode ||
    item.OpportunityType ||
    ''
  );
}

function getCurrencyUnit(maxValue) {
  return maxValue >= 1000000 ? 'M' : 'K';
}

function formatCurrencyTick(value, unit) {
  if (unit === 'M') return `$${(value / 1000000).toFixed(value % 1000000 === 0 ? 0 : 1)}M`;
  return `$${Math.round(value / 1000)}K`;
}

function buildTicks(maxValue) {
  if (!maxValue || maxValue <= 0) return [0, 1000];
  const raw = maxValue / 4;
  const magnitude = 10 ** Math.floor(Math.log10(raw || 1));
  const normalized = raw / magnitude;
  let step = magnitude;
  if (normalized > 5) step = 10 * magnitude;
  else if (normalized > 2) step = 5 * magnitude;
  else if (normalized > 1) step = 2 * magnitude;
  const ceiling = Math.ceil(maxValue / step) * step;
  const ticks = [];
  for (let i = 0; i <= 4; i += 1) ticks.push((ceiling / 4) * i);
  return ticks;
}

const BottomRightChart = ({ detailData, selectedTopRightItems, Probability, handleFilter, selectedType, selectedDirector, searchRightStr }) => {
  const wrapRef = useRef(null);
  const chartWidth = useMeasuredWidth(wrapRef);
  const [localSearchRightStr, setLocalSearchRightStr] = useState(searchRightStr || '');
  const [selectedKeys, setSelectedKeys] = useState([]);

  useEffect(() => { setLocalSearchRightStr(searchRightStr || ''); }, [searchRightStr]);
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if ((searchRightStr || '') !== localSearchRightStr) handleFilter({ searchRightStr: localSearchRightStr });
    }, 200);
    return () => clearTimeout(timeoutId);
  }, [localSearchRightStr, searchRightStr, handleFilter]);

  const { rows, projectTypes, directors, maxRevenue, nameWidth, directorWidth, typeWidth } = useMemo(() => {
    const filterPercent = (selectedTopRightItems || []).map((d) => d.percent);
    const projectTypeSet = new Set();
    const directorSet = new Set();
    const rowMap = new Map();
    const rx = searchRightStr ? new RegExp(searchRightStr, 'i') : null;

    (detailData?.Right || []).forEach((item) => {
      const bucket = getProbabilityBucket(item.ProbabilityOfSuccess);
      const typeValue = getTypeLabel(item);
      const directorValue = item.Director || '';
      const nameValue = item.Name || '';
      const revenueValue = Number(item.Revenue || 0);
      projectTypeSet.add(typeValue);
      directorSet.add(directorValue);

      const matchesType = selectedType === 'All' || !selectedType || `${selectedType}` === `${typeValue}`;
      const matchesDirector = selectedDirector === 'All' || !selectedDirector || selectedDirector === directorValue;
      const matchesSearch = !searchRightStr || (rx && rx.test(nameValue));
      const matchesPercent = filterPercent.length === 0 || filterPercent.includes(bucket);
      const probabilityValue = Number(item.ProbabilityOfSuccess || 0);
      const normalizedProbability = probabilityValue > 1 ? probabilityValue : Math.round(probabilityValue * 100);
      const selectedProbability = Probability === 'All' ? 'All' : Number(Probability);
      const matchesProbability = selectedProbability === 'All' || selectedProbability === normalizedProbability;
      if (!matchesType || !matchesDirector || !matchesSearch || !matchesPercent || !matchesProbability) return;

      const key = `${item.OpportunityId || nameValue}||${typeValue}||${directorValue}`;
      if (!rowMap.has(key)) rowMap.set(key, { Key: key, Name: nameValue, Director: shortDirectorName(directorValue), DirectorFull: directorValue, Type: typeValue, Revenue: 0, ProbabilityBucket: bucket });
      rowMap.get(key).Revenue += revenueValue;
    });

    const nextRows = Array.from(rowMap.values()).sort((a, b) => b.Revenue - a.Revenue);
    const dynamicNameWidth = clamp(Math.max(MIN_NAME_WIDTH, ...nextRows.map((row) => estimateTextWidth(row.Name, 7.2, 28))), MIN_NAME_WIDTH, MAX_NAME_WIDTH);
    const dynamicDirectorWidth = clamp(Math.max(MIN_DIRECTOR_WIDTH, ...nextRows.map((row) => estimateTextWidth(row.Director, 7.0, 24))), MIN_DIRECTOR_WIDTH, MAX_DIRECTOR_WIDTH);
    const dynamicTypeWidth = clamp(Math.max(MIN_TYPE_WIDTH, ...nextRows.map((row) => estimateTextWidth(row.Type, 7.0, 20))), MIN_TYPE_WIDTH, MAX_TYPE_WIDTH);

    return {
      rows: nextRows,
      projectTypes: Array.from(projectTypeSet).sort((a, b) => `${a}`.localeCompare(`${b}`)),
      directors: Array.from(directorSet).sort(),
      maxRevenue: nextRows.reduce((max, row) => Math.max(max, row.Revenue), 0),
      nameWidth: dynamicNameWidth,
      directorWidth: dynamicDirectorWidth,
      typeWidth: dynamicTypeWidth,
    };
  }, [detailData, selectedTopRightItems, selectedType, selectedDirector, searchRightStr, Probability]);

  const ticks = useMemo(() => buildTicks(maxRevenue), [maxRevenue]);
  const axisMax = ticks[ticks.length - 1] || maxRevenue || 1000;
  const totalVisibleRevenue = rows.reduce((sum, row) => sum + (Number(row.Revenue) || 0), 0);
  const currencyUnit = getCurrencyUnit(totalVisibleRevenue);
  const chartAreaWidth = Math.max(chartWidth - nameWidth - directorWidth - typeWidth - AXIS_RIGHT_PADDING - 4, 180);

  const getBarColor = (row) => {
    const palette = PROBABILITY_PALETTES[row.ProbabilityBucket] || PROBABILITY_PALETTES[0];
    if (selectedKeys.length === 0) return palette.active;
    return selectedKeys.includes(row.Key) ? palette.active : palette.inactive;
  };

  const toggleRowSelection = (event, row) => {
    setSelectedKeys((prev) => {
      if (event.shiftKey) return prev.includes(row.Key) ? prev.filter((key) => key !== row.Key) : [...prev, row.Key];
      return prev.length === 1 && prev[0] === row.Key ? [] : [row.Key];
    });
  };

  return (
    <div className="well" style={{ paddingTop: 30, height: '100%' }}>
      <strong style={{ fontFamily: 'Verdana', fontSize: '12px' }}>Potential Fees by Project</strong>
      <div style={{ paddingTop: 10 }} />

      <Box display="flex" alignItems="center" gap={2}>
        <FormControlLabel
          label="Project Type"
          labelPlacement="start"
          sx={{ marginLeft: '0px !important', '& .MuiTypography-root': { fontWeight: 'bold', fontFamily: 'Verdana', fontSize: '10pt' } }}
          control={<FormControl variant="standard"><Select value={selectedType} onChange={(event) => handleFilter({ selectedType: event.target.value })} sx={{ '& .MuiInputBase-input': { padding: 0, paddingRight: 3, fontWeight: 'bold', fontFamily: 'Verdana', fontSize: '10pt' } }}>{[<MenuItem value="All" key="all">All</MenuItem>, ...projectTypes.map((item) => <MenuItem key={`type-${item}`} value={`${item}`}>{item}</MenuItem>)]}</Select></FormControl>}
        />
        <FormControlLabel
          label="Director"
          labelPlacement="start"
          sx={{ marginLeft: '0px !important', '& .MuiTypography-root': { fontWeight: 'bold', fontFamily: 'Verdana', fontSize: '10pt' } }}
          control={<FormControl variant="standard"><Select value={selectedDirector} onChange={(event) => handleFilter({ selectedDirector: event.target.value })} sx={{ '& .MuiInputBase-input': { padding: 0, paddingRight: 3, fontWeight: 'bold', fontFamily: 'Verdana', fontSize: '10pt' } }}>{[<MenuItem value="All" key="all">All</MenuItem>, ...directors.map((item) => <MenuItem key={`director-${item}`} value={`${item}`}>{item}</MenuItem>)]}</Select></FormControl>}
        />
        <Box display="flex" alignItems="center" gap={1}>
          <span style={{ fontWeight: 'bold', fontFamily: 'Verdana', fontSize: '12px' }}>Opportunity Name</span>
          <input type="text" name="OppName" style={{ height: 18, width: 125, paddingTop: 2 }} value={localSearchRightStr} onChange={(event) => setLocalSearchRightStr(event.target.value)} />
        </Box>
      </Box>

      <div ref={wrapRef} id="or_bottom" style={{ maxHeight: '36vh', overflow: 'auto', borderTop: 'solid 1px #a9a9a9', width: '100%' }}>
        {rows.map((row) => {
          const width = axisMax > 0 ? Math.max((row.Revenue / axisMax) * chartAreaWidth, row.Revenue > 0 ? 2 : 0) : 0;
          const selected = selectedKeys.includes(row.Key);
          return (
            <div key={row.Key} onClick={(event) => toggleRowSelection(event, row)} title={`${row.Name} | ${row.DirectorFull || row.Director} | ${thousandFormat2(row.Revenue)}`} style={{ display: 'flex', alignItems: 'center', height: ROW_HEIGHT, userSelect: 'none', borderLeft: 'solid 1px #a9a9a9', borderRight: 'solid 1px #a9a9a9', borderBottom: 'solid 1px #a9a9a9', cursor: 'pointer', backgroundColor: selected ? '#f7f7f7' : 'transparent' }}>
              <div style={{ width: nameWidth, paddingLeft: 2, paddingRight: 6, fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.Name}</div>
              <div style={{ width: directorWidth, paddingLeft: 1, paddingRight: 6, fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.Director}</div>
              <div style={{ width: typeWidth, paddingLeft: 1, paddingRight: 6, fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.Type}</div>
              <div style={{ flex: 1, height: '100%', position: 'relative', overflow: 'hidden' }}><div style={{ position: 'absolute', left: 0, top: 2, height: 19, width, backgroundColor: getBarColor(row) }} /></div>
            </div>
          );
        })}
      </div>

      {rows.length > 0 && (
        <div style={{ width: '100%', paddingTop: 2 }}>
          <div style={{ display: 'flex', width: '100%' }}>
            <div style={{ width: nameWidth + directorWidth + typeWidth + 1 }} />
            <div style={{ flex: 1, position: 'relative', height: 20, overflow: 'visible', paddingRight: AXIS_RIGHT_PADDING }}>
              {ticks.map((tick, idx) => {
                const left = axisMax > 0 ? (tick / axisMax) * chartAreaWidth : 0;
                const transform = idx === 0 ? 'translateX(0)' : idx === ticks.length - 1 ? 'translateX(-100%)' : 'translateX(-50%)';
                const textAlign = idx === 0 ? 'left' : idx === ticks.length - 1 ? 'right' : 'center';
                return (
                  <div
                    key={`tick-${tick}-${idx}`}
                    style={{
                      position: 'absolute',
                      left,
                      top: 0,
                      transform,
                      fontSize: 11,
                      textAlign,
                      whiteSpace: 'nowrap',
                      overflow: 'visible',
                      pointerEvents: 'none',
                    }}
                  >
                    <div style={{ borderLeft: '1px solid black', height: 5, margin: idx === 0 ? '0' : idx === ticks.length - 1 ? '0 0 0 auto' : '0 auto' }} />
                    <div style={{ paddingTop: 1 }}>{formatCurrencyTick(tick, currencyUnit)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

BottomRightChart.propTypes = {
  detailData: PropTypes.object,
  selectedTopRightItems: PropTypes.array,
  Probability: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleFilter: PropTypes.func.isRequired,
  selectedType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedDirector: PropTypes.string,
  searchRightStr: PropTypes.string,
};

BottomRightChart.defaultProps = {
  detailData: null,
  selectedTopRightItems: [],
  Probability: 'All',
  selectedType: 'All',
  selectedDirector: 'All',
  searchRightStr: '',
};

export default BottomRightChart;
